import type { EncounterNoteAddendum } from "@emr/contracts";

/** clinical.encounters statuses that mean the visit is no longer open. */
const closedEncounterStatuses = new Set(["completed", "closed", "signed", "cancelled", "left"]);

export function isClosedEncounter(status: string | undefined): boolean {
  return closedEncounterStatuses.has((status ?? "").toLowerCase());
}

export function visitDateLabel(iso: string): string {
  const parsed = new Date(iso);
  // Local calendar day — slicing the ISO string would show the UTC day.
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toLocaleDateString("en-CA");
}

/**
 * A date the clinic recorded as a calendar day, printed as that day. An
 * imported visit's day was fixed at the previous clinic: re-deriving it from
 * an instant would move a late-evening appointment across midnight for a
 * reader in another zone, and a history that reads differently in Vancouver
 * than in Toronto is not a history.
 */
export function calendarDateLabel(date: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : "";
}

export function visitTimeLabel(iso: string): string {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime())
    ? ""
    : parsed.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/** "Addendum 2 · Dr. Riley Chen · 2026-10-12 14:05": which one, who signed it, and when. */
export function addendumHeading(addendum: EncounterNoteAddendum): string {
  const signedAt = [visitDateLabel(addendum.signedAt), visitTimeLabel(addendum.signedAt)]
    .filter(Boolean)
    .join(" ");
  return [`Addendum ${addendum.sequence}`, addendum.signer, signedAt].filter(Boolean).join(" · ");
}

export function initialsFor(firstName: string, lastName: string): string {
  return `${firstName.trim()[0] ?? ""}${lastName.trim()[0] ?? ""}`.toUpperCase();
}
